import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchVenueConfig } from "../APIs/Venue/GetVenueConfig";
import { fetchVenues } from "../APIs/Venue/GetVenues";
import type { BookingStage, VenueConfig, VenuePage } from "../types/venues";
import { getBookingStages } from "../utils/booking";
import { useVenueGroupConfig } from "./useOrganisationData";
import { venueConfigQueryKey, venuesQueryKey } from "../queries/venueQueryKeys";
import { useAppSelector } from "../store/hooks";
import { selectLoadedVenueId } from "../store/slices/diarySlice";

export { venueConfigQueryKey, venuesQueryKey };

// Returned once the venue data has been cleared (no venues, or an
// organisation switch), so readers see the same "loaded but empty" values the
// diary has always used.
const EMPTY_VENUE_CONFIG: VenueConfig = {};
const NO_VENUES: VenuePage[] = [];
const NO_STAGES: BookingStage[] = [];

// The readers below never fetch (`enabled: false`), but they pass the real
// `queryFn` because a query refetches with the options of its most recently
// rendered observer, so an invalidation must still be able to fetch.

/**
 * Config of the venue the diary is showing, read from the
 * `["venueConfig", loadedVenueId]` query. During a venue switch it stays on
 * the previous venue until the new config has loaded.
 */
export const useVenueConfig = (): VenueConfig | null => {
  const loadedVenueId = useAppSelector(selectLoadedVenueId);
  const { data } = useQuery<VenueConfig | undefined>({
    queryKey: venueConfigQueryKey(loadedVenueId ?? undefined),
    queryFn: () => fetchVenueConfig(loadedVenueId ?? undefined),
    enabled: false
  });

  if (loadedVenueId === null) return EMPTY_VENUE_CONFIG;
  if (loadedVenueId === undefined) return null;
  return data ?? null;
};

/**
 * Venues of the current organisation, read from the `["venues", groupId]`
 * query. Untitled venues are left out once a venue config has been applied.
 */
export const useVenues = (): VenuePage[] => {
  const loadedVenueId = useAppSelector(selectLoadedVenueId);
  const venueGroupId = useVenueGroupConfig()?._id;
  const { data } = useQuery<VenuePage[] | undefined>({
    queryKey: venuesQueryKey(venueGroupId),
    queryFn: () => fetchVenues(venueGroupId),
    enabled: false
  });

  return useMemo(() => {
    if (!data) return NO_VENUES;
    if (loadedVenueId === undefined) return data;
    return data.filter((venue) => !!venue.title);
  }, [loadedVenueId, data]);
};

/** Booking stages of the loaded venue (venue overrides, else the group's). */
export const useBookingStages = (): BookingStage[] | undefined => {
  const loadedVenueId = useAppSelector(selectLoadedVenueId);
  const venueConfig = useVenueConfig();
  const venueGroupConfig = useVenueGroupConfig();

  return useMemo(() => {
    if (loadedVenueId === null) return NO_STAGES;
    if (!venueConfig || !venueGroupConfig) return undefined;
    return getBookingStages({ venueConfig, venueGroupConfig });
  }, [loadedVenueId, venueConfig, venueGroupConfig]);
};
